<template>
	<div class="form-container u-container">
		<section>
			<h2 class="form-title">개인 정보</h2>

			<div class="form-group">
				<label class="form-label">이름</label>
				<div class="columns">
					<div class="column col-auto">
						<div class="form-text">{{ user.user_name }}</div>
					</div>

					<div v-if="!isRegularUser" class="column col-auto col-ml-auto">
						<Btn @click="onClickUpdateToRegular" color="secondary" medium block
							>정회원 전환하기</Btn
						>
					</div>
				</div>
			</div>

			<div class="form-group">
				<label class="form-label">휴대전화 번호</label>

				<div class="columns">
					<div class="column col-auto">
						<div class="form-text">{{ user.user_hp }}</div>
					</div>
					<div class="column col-auto col-ml-auto">
						<Btn
							@click="toggleUpdatePhoneNumberForm"
							color="secondary"
							medium
							block
							>변경</Btn
						>
					</div>
				</div>

				<!-- 휴대전화 번호 변경 -->
				<div v-if="isOpenUpdatePhoneNumber">
					<div :class="{ 'has-error': validMessage.userHp }">
						<div class="columns">
							<div class="column col-7">
								<input
									v-model="userHp"
									class="form-input"
									type="text"
									placeholder="- 없이 숫자만 입력"
									:readonly="isCertSuccess"
								/>
							</div>
							<div class="column col-5">
								<Btn
									v-if="!isSendCertSms"
									@click="onClickSendCertSms"
									type="button"
									color="primary"
									medium
									block
									:disabled="validMessage.userHp !== ''"
								>
									인증번호 받기
								</Btn>
								<Btn
									v-else
									@click="onClickSendCertSms"
									type="button"
									color="secondary"
									medium
									block
									:disabled="validMessage.userHp !== '' || isCertSuccess"
								>
									인증번호 재발송
								</Btn>
							</div>
						</div>

						<FormInputHint :message="validMessage.userHp"></FormInputHint>
					</div>

					<!-- 인증번호 전송 성공 후 노출 -->
					<div v-if="isSendCertSms">
						<div class="columns">
							<div class="column col-12">
								<input
									v-model="certNumber"
									class="form-input"
									type="text"
									placeholder="인증번호 입력"
									:class="{
										'is-success': isCertSuccess,
										'is-error': !isCertSuccess && validCertNumber,
									}"
									:readonly="isCertSuccess"
								/>

								<FormInputHint :message="validCertNumber"></FormInputHint>
							</div>
							<div v-if="!isCertSuccess" class="column col-12">
								<Btn
									@click="onClickCheckCertNumber"
									type="button"
									color="primary"
									medium
									block
									:disabled="!certNumber"
									>확인
								</Btn>
							</div>
						</div>
					</div>
				</div>
			</div>
		</section>

		<hr class="form-hr" />

		<section>
			<h2 class="form-title">로그인 정보</h2>
			<div class="form-group">
				<label class="form-label">아이디</label>
				<div class="form-text">{{ user.user_id }}</div>
			</div>

			<div class="form-group">
				<label class="form-label">비밀번호</label>

				<div v-if="!isOpenUpdatePw" class="columns">
					<div class="column col-auto">
						<div class="form-text">
							<i class="ri-checkbox-blank-circle-fill"></i
							><i class="ri-checkbox-blank-circle-fill"></i
							><i class="ri-checkbox-blank-circle-fill"></i
							><i class="ri-checkbox-blank-circle-fill"></i
							><i class="ri-checkbox-blank-circle-fill"></i
							><i class="ri-checkbox-blank-circle-fill"></i
							><i class="ri-checkbox-blank-circle-fill"></i
							><i class="ri-checkbox-blank-circle-fill"></i
							><i class="ri-checkbox-blank-circle-fill"></i>
						</div>
					</div>
					<div class="column col-auto col-ml-auto">
						<Btn @click="openUpdatePwForm" color="secondary" medium block
							>변경</Btn
						>
					</div>
				</div>

				<!-- 비밀번호 변경 -->
				<form v-else @submit.prevent="updatePassword">
					<div class="columns">
						<div class="column col-12">
							<input
								v-model="currentPassword"
								class="form-input"
								type="password"
								placeholder="현재 비밀번호를 입력하세요"
							/>
						</div>
						<div class="column col-12">
							<input
								v-model="newPassword"
								class="form-input"
								type="password"
								placeholder="새 비밀번호를 입력하세요"
							/>
						</div>
						<div class="column col-12">
							<input
								v-model="reNewPassword"
								class="form-input"
								type="password"
								placeholder="새 비밀번호를 한번 더 확인하세요"
							/>
						</div>
					</div>
					<div class="columns">
						<div class="column col-6">
							<Btn @click="closeUpdatePwForm" color="secondary" medium block
								>변경 취소</Btn
							>
						</div>
						<div class="column col-6">
							<Btn type="submit" color="primary" medium block>확인</Btn>
						</div>
					</div>
				</form>
			</div>
		</section>

		<hr class="form-hr" />

		<section>
			<form @submit.prevent="updateUserForm">
				<div class="form-group">
					<label class="form-label"
						>이메일 <span class="form-help form-help--required">*</span></label
					>
					<input v-model="userEmail" class="form-input" type="text" />
				</div>

				<div class="form-group">
					<label class="form-label">생년월일</label>
					<div class="form-text">
						<div v-if="userBirth">{{ userBirth }} {{ birthCalendar }}</div>
						<div v-else>입력 안함</div>
					</div>
				</div>

				<div class="form-group">
					<label class="form-label">성별</label>
					<div class="form-text">
						{{ gender }}
					</div>
				</div>

				<div v-if="isRegularUser" class="form-group">
					<div class="form-label">
						관리 주기 <span class="form-help form-help--required">*</span>
						<p class="form-help u-color-red mb-1">
							※ 선택하신 관리주기에 맞춰 관리예정일로부터<br />
							3일 전에 관리시기알림 푸시메시지가 자동 발송됩니다.
						</p>
					</div>
					<div class="columns">
						<div class="column col-12">
							<select v-model="asTermSelect" class="form-select">
								<option value="">관리 주기 선택</option>
								<option
									v-for="item in asTermList"
									:key="item.key"
									:value="item.value"
								>
									{{ item.name }}
								</option>
							</select>
						</div>
						<div class="column col-12">
							<input
								v-model="asTermOther"
								ref="asTermOther"
								class="form-input"
								type="number"
								min="1"
								max="99999"
								placeholder="관리 일수(숫자만 입력)"
								:readonly="isAsTermOtherReadOnly"
							/>
						</div>
					</div>
				</div>

				<div class="form-group">
					<label class="form-label">주소</label>
					<div class="columns">
						<div class="column col-7">
							<input
								v-model="zipcode"
								class="form-input"
								type="text"
								placeholder="우편번호"
								readonly
							/>
						</div>
						<div class="column col-5">
							<Btn
								@click="togglePostcodePopup"
								type="button"
								color="primary"
								medium
								block
								>우편번호 찾기
							</Btn>
						</div>
						<div v-show="isShowPostcodePopup" class="column col-12">
							<vue-daum-postcode
								style="height: 300px; overflow-y: auto"
								@complete="completeAddress($event)"
								:no-auto-mapping="false"
							></vue-daum-postcode>
						</div>
						<div class="column col-12">
							<input
								v-model="userAddr1"
								class="form-input"
								type="text"
								placeholder="주소"
								readonly
							/>
						</div>
						<div class="column col-12">
							<input
								v-model="userAddr2"
								ref="userAddr2"
								class="form-input"
								type="text"
								placeholder="상세 주소"
							/>
						</div>
					</div>
				</div>

				<div class="form-group">
					<label class="form-label">이용 지역/지점</label>
					<div class="columns">
						<div class="column col-6">
							<select
								@change="changeBranchArea"
								v-model="branchPlace1"
								class="form-select"
							>
								<option value="">이용 지역</option>
								<option
									v-for="item in branchArea"
									:key="item.uid"
									:value="item.category_name"
								>
									{{ item.category_name }}
								</option>
							</select>
						</div>
						<div class="column col-6">
							<select v-model="branchPlace2" class="form-select">
								<option value="">이용 지점</option>
								<option
									v-for="item in branch"
									:key="item.uid"
									:value="item.mini_company"
								>
									{{ item.mini_company }}
								</option>
							</select>
						</div>
					</div>
				</div>

				<div class="form-group">
					<label class="form-label">방문 경로</label>
					<select v-model="visitChannel" class="form-select">
						<option value="">방문 경로</option>
						<option v-for="item in visitPath" :key="item.key" :value="item.key">
							{{ item.name }}
						</option>
					</select>
				</div>

				<div class="form-group">
					<div class="columns flex-centered">
						<div class="column col-auto mb-0">
							<span class="form-label">이벤트 및 제품 정보 수신 동의</span>
						</div>
						<div class="column col-auto col-ml-auto mb-0"></div>
					</div>

					<div class="columns">
						<div class="column col-4 mb-0">
							<label class="form-checkbox">
								<input v-model="userSms" type="checkbox" />
								<i class="form-icon"></i> SMS
							</label>
						</div>
						<div class="column col-4 mb-0">
							<label class="form-checkbox">
								<input v-model="userMailing" type="checkbox" />
								<i class="form-icon"></i> 이메일
							</label>
						</div>
					</div>
				</div>

				<div class="form-group">
					<label class="form-inline form-switch">
						<input v-model="userIsPushOn" type="checkbox" />
						<i class="form-icon"></i> 앱 알림 설정
					</label>
				</div>

				<div class="form-group form-button">
					<Btn type="submit" color="primary" x-large block>저장</Btn>
				</div>
			</form>
		</section>

		<div class="form-group form-button">
			<Btn
				@click="$router.push({ name: 'userLeave' })"
				type="submit"
				color="link"
				medium
				>회원 탈퇴</Btn
			>
		</div>
	</div>
</template>

<script>
import Vue from 'vue';

import VueDaumPostcode from 'vue-daum-postcode';

Vue.use(VueDaumPostcode);

import validation from '@/utils/validation';
import {
	updateToRegularUserLevel,
	editUserPhoneNumberUpdate,
	editUserPasswordUpdate,
	editUserFormUpdate,
	sendCertSms,
	checkCertNumber,
	isValidUserHp,
	fetchBranchArea,
	fetchBranchFromAreaName,
	fetchVisitPath,
} from '@/api';

import mixin from '@/mixins/mixin';

import Btn from '@/components/common/Btn';
import FormInputHint from '@/components/common/FormInputHint';

export default {
	mixins: [mixin],
	components: {
		Btn,
		FormInputHint,
	},
	data() {
		return {
			userHp: '',
			certNumber: '',
			isOpenUpdatePhoneNumber: false,
			// 인증번호 발송 여부
			isSendCertSms: false,
			// 인증 완료 여부
			isCertSuccess: false,

			isOpenUpdatePw: false,
			currentPassword: '',
			newPassword: '',
			reNewPassword: '',

			userEmail: '',

			asTermSelect: '',
			asTermOther: '',
			isAsTermOtherReadOnly: true,

			zipcode: '',
			userAddr1: '',
			userAddr2: '',

			// 주소찾기 팝업 노출 여부
			isShowPostcodePopup: false,

			// 이용 지역/지점
			branchPlace1: '',
			branchPlace2: '',

			branchArea: [],
			branch: [],

			// 방문 경로
			visitChannel: '',
			visitPath: [],

			// 유효성 검사
			validMessage: {},
			validCertNumber: '',

			// 이벤트 수신 동의
			userSms: false,
			userMailing: false,

			// 앱 푸시 알림 on 여부
			userIsPushOn: false,
		};
	},
	watch: {
		userHp(val) {
			this.validMessage.userHp = validation.getValidUserHp(val);
		},
		asTermSelect(val) {
			this.isAsTermOtherReadOnly = val !== 'other';

			if (val === 'other') {
				this.$refs.asTermOther.focus();
			} else {
				this.asTermOther = '';
			}
		},
	},
	computed: {
		user() {
			return this.$store.state.userStore.user;
		},
		isRegularUser() {
			return this.$store.getters['userStore/isRegularUser'];
		},
		userId() {
			return this.$store.getters['userStore/userId'];
		},
		userBirth() {
			let userBirth = this.$store.state.userStore.user.user_birth;
			userBirth = userBirth.replace(/[^0-9]/g, '');

			if (userBirth) {
				const year = userBirth.substr(0, 4);
				const month = userBirth.substr(4, 2);
				const day = userBirth.substr(6, 2);

				userBirth = `${year}.${month}.${day}`;
			}

			return userBirth;
		},
		birthCalendar() {
			let str = '';

			const storeBirthCalendar =
				this.$store.state.userStore.user.birth_calendar;

			if (storeBirthCalendar === 'S') {
				str = '양력';
			}
			if (storeBirthCalendar === 'D') {
				str = '음력';
			}

			return str;
		},
		gender() {
			let str = '선택안함';

			const userSex = this.$store.state.userStore.user.user_sex;

			if (userSex === 'M') {
				str = '남자';
			}
			if (userSex === 'F') {
				str = '여자';
			}

			return str;
		},
		asTermList() {
			return this.$store.getters['asStore/asTermList'];
		},
		userAsTerm() {
			return this.$store.getters['asStore/userAsTerm'];
		},
	},
	methods: {
		init() {
			this.setBranchArea();
			this.setVisitPath();

			this.setData();

			if (this.branchPlace1) this.setBranch();

			if (this.isRegularUser) this.renderAsTerm();
		},
		setData() {
			this.userEmail = this.$store.state.userStore.user.user_email;

			this.zipcode = `${this.$store.state.userStore.user.user_zip1}${this.$store.state.userStore.user.user_zip2}`;
			this.userAddr1 = this.$store.state.userStore.user.user_addr1;
			this.userAddr2 = this.$store.state.userStore.user.user_addr2;

			this.branchPlace1 = this.$store.state.userStore.user.branch_place1;
			this.branchPlace2 = this.$store.state.userStore.user.branch_place2;

			this.visitChannel = this.$store.state.userStore.user.visit_channel;

			if (this.$store.state.userStore.user.user_sms === '1') {
				this.userSms = true;
			}
			if (this.$store.state.userStore.user.user_mailing === '1') {
				this.userMailing = true;
			}
			if (this.$store.state.userStore.user.user_is_push_on === '1') {
				this.userIsPushOn = true;
			}
		},
		// 휴대전화 번호 변경 폼 토글
		toggleUpdatePhoneNumberForm() {
			this.isOpenUpdatePhoneNumber = !this.isOpenUpdatePhoneNumber;
		},
		onClickSendCertSms() {
			this.checkIsValidUserHp();
		},
		async checkIsValidUserHp() {
			const params = {
				userHp: this.userHp,
			};

			const { data } = await isValidUserHp(params);

			if (data.code === '00000') {
				this.sendCertSmsToUserHp();
			} else {
				const message = data.message;
				const options = {
					okLabel: '확인',
					message,
				};
				this.showAlert(options);
			}
		},
		async sendCertSmsToUserHp() {
			const params = {
				userName: this.user.user_name,
				userHp: this.userHp,
			};

			const { data } = await sendCertSms(params);

			if (data.code === '00000') {
				this.isSendCertSms = true;
			} else {
				const message = data.message;
				const options = {
					message,
				};
				this.showAlert(options);
			}
		},
		// 발송된 인증번호 체크
		async onClickCheckCertNumber() {
			const params = {
				userName: this.user.user_name,
				userHp: this.userHp,
				certNumber: this.certNumber,
			};

			const { data } = await checkCertNumber(params);

			this.validCertNumber = data.message;

			// 인증 완료
			if (data.code === '00000') {
				this.isCertSuccess = true;
				this.updatePhoneNumber();
			}
		},
		// 휴대전화 번호 변경
		async updatePhoneNumber() {
			const params = {
				userId: this.user.user_id,
				userHp: this.userHp,
				isRegularUser: this.isRegularUser,
			};

			const { data } = await editUserPhoneNumberUpdate(params);

			const message = data.message;
			const options = {
				okLabel: '확인',
				message,
			};

			const dialog = this.showAlert(options);

			if (data.code === '00000') {
				// 유저 스토어에 변경된 휴대전화 번호 commit
				dialog.then(() => {
					this.$store.commit('userStore/setUserHp', data.userHp);
				});

				// 휴대전화 번호 변경 폼 init
				this.initPhoneCertForm();
			}
		},
		initPhoneCertForm() {
			this.isOpenUpdatePhoneNumber = false;

			this.isSendCertSms = false;
			this.isCertSuccess = false;

			this.userHp = '';
			this.validMessage.userHp = '';
		},
		// 비밀번호 변경 폼 오픈
		openUpdatePwForm() {
			this.initUpdatePwForm();
			this.isOpenUpdatePw = true;
		},
		// 비밀번호 변경 폼 클로즈
		closeUpdatePwForm() {
			this.initUpdatePwForm();
			this.isOpenUpdatePw = false;
		},
		// 비밀번호 변경 폼 초기화
		initUpdatePwForm() {
			this.currentPassword = '';
			this.newPassword = '';
			this.reNewPassword = '';
		},
		// 비밀번호 변경
		async updatePassword() {
			const params = {
				userId: this.user.user_id,
				currentPassword: this.currentPassword,
				newPassword: this.newPassword,
				reNewPassword: this.reNewPassword,
			};

			const invalidMessage = validation.getValidUserPw(
				this.newPassword,
				this.user.user_id,
			);

			if (invalidMessage) {
				const options = {
					okLabel: '확인',
					message: invalidMessage,
				};
				this.showAlert(options);

				return false;
			}

			const { data } = await editUserPasswordUpdate(params);

			const message = data.message;
			const options = {
				okLabel: '확인',
				message,
			};
			this.showAlert(options).then(() => {
				if (data.code === '00000') {
					this.closeUpdatePwForm();
				}
			});
		},
		// 사용자 정보 변경
		async updateUserForm() {
			const params = {
				userId: this.user.user_id,
				userSms: this.userSms,
				userMailing: this.userMailing,
				userEmail: this.userEmail,
				userIsPushOn: this.userIsPushOn,
				zipcode: this.zipcode,
				userAddr1: this.userAddr1,
				userAddr2: this.userAddr2,
				branchPlace1: this.branchPlace1,
				branchPlace2: this.branchPlace2,
				visitChannel: this.visitChannel,
				isRegularUser: this.isRegularUser,
				asTermSelect: this.asTermSelect,
				asTermOther: this.asTermOther,
			};

			const { data } = await editUserFormUpdate(params);

			const message = data.message;
			const options = {
				message,
			};
			this.showAlert(options);
		},
		togglePostcodePopup() {
			this.showPostcodePopup(!this.isShowPostcodePopup);
		},
		completeAddress(result) {
			this.zipcode = result.zonecode;
			this.userAddr1 = result.address;

			this.showPostcodePopup(false);

			// 상세주소 입력란 포커스
			this.$refs.userAddr2.focus();
		},
		showPostcodePopup(show) {
			this.isShowPostcodePopup = show;
		},
		// 이용 지역 리스트
		async setBranchArea() {
			const { data } = await fetchBranchArea();

			if (data.code === '00000') {
				this.branchArea = data.branchArea || [];
			} else {
				this.branchArea = [];
			}
		},
		// 이용 지점 리스트
		async setBranch() {
			const params = {
				categoryName: this.branchPlace1,
			};

			const { data } = await fetchBranchFromAreaName(params);

			if (data.code === '00000') {
				this.branch = data.branchFromAreaName || [];
			} else {
				this.branch = [];
			}
		},
		// 방문 경로 리스트
		async setVisitPath() {
			const { data } = await fetchVisitPath();
			this.visitPath = data;
		},
		// 이용 지역 변경
		changeBranchArea() {
			this.initBranch();
			this.setBranch();
		},
		// 이용 지점 init
		initBranch() {
			this.branchPlace2 = '';
		},
		async onClickUpdateToRegular() {
			const params = {
				userId: this.user.user_id,
				userName: this.user.user_name,
				userHp: this.user.user_hp,
			};

			const { data } = await updateToRegularUserLevel(params);

			const message = data.message;
			const options = {
				okLabel: '확인',
				message,
			};
			this.showAlert(options);
		},
		async renderAsTerm() {
			await this.$store.dispatch('asStore/getAsTermList');
			this.fetchUserAsTerm();
		},
		async fetchUserAsTerm() {
			const payload = {
				userId: this.userId,
			};

			const { data } = await this.$store.dispatch(
				'asStore/getUserAsTerm',
				payload,
			);

			if (data.code === '00000') {
				this.setUserAsTerm();
			}
		},
		setUserAsTerm() {
			const asCycle = this.userAsTerm.AS_CYCLE;
			const userAsTerm = this.asTermList.find(el => el.value === asCycle);

			if (userAsTerm) {
				this.asTermSelect = asCycle;
			} else {
				this.asTermSelect = 'other';
				this.isAsTermOtherReadOnly = false;
				this.asTermOther = asCycle;
			}
		},
	},
	created() {
		this.init();
	},
};
</script>

<style>
.vue-daum-postcode-container {
	height: 100% !important;
}
</style>
